import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { StatusDot } from "@/components/brand/status-dot";
import type { ContextDraft, Evidence } from "../../domain/draft.ts";
import { useNav } from "../nav.tsx";
import { ErrorNote, errText, Field, SectionTitle } from "../ui.tsx";
import type { StageProps } from "./EmployeeShell.tsx";

type TextKey = "tagline" | "focus" | "outOfScope" | "operatingNotes" | "knowledgeDigest";
const TEXT_FIELDS: { key: TextKey; label: string; hint: string; rows: number }[] = [
  { key: "tagline", label: "Tagline", hint: "One line for the marketplace card.", rows: 1 },
  { key: "focus", label: "Focus", hint: "What the employee owns and how it works. Becomes its charter's FOCUS.", rows: 8 },
  { key: "outOfScope", label: "Never do / out of scope", hint: "Firm rules. Becomes OUT OF SCOPE.", rows: 6 },
  { key: "operatingNotes", label: "Operating notes", hint: "Tone, style, escalation etiquette.", rows: 4 },
  { key: "knowledgeDigest", label: "Knowledge digest", hint: "What it must know by heart. The full sources ship with the release for lookup.", rows: 10 },
];

function EvidenceList({ evidence }: { evidence: Evidence[] }) {
  if (!evidence.length) return <p className="text-xs text-muted-foreground">No evidence cited.</p>;
  return (
    <details className="text-xs text-muted-foreground">
      <summary className="cursor-pointer select-none">
        {evidence.length} source{evidence.length === 1 ? "" : "s"} cited
      </summary>
      <ul className="mt-1 grid gap-1">
        {evidence.map((e, i) => (
          <li key={i}>
            <Badge variant="outline" className="mr-1">
              {e.kind}
            </Badge>
            {e.quote ? `“${e.quote}”` : e.ref}
          </li>
        ))}
      </ul>
    </details>
  );
}

const lines = (s: string) => s.split("\n").map((x) => x.trim()).filter(Boolean);

export function ReviewStage({ api, detail, reload }: StageProps) {
  const { go } = useNav();
  const e = detail.employee;
  const [data, setData] = useState<Awaited<ReturnType<typeof api.draft>>>();
  const [draft, setDraft] = useState<ContextDraft>();
  const [catalog, setCatalog] = useState<{ id: string; summary: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const load = async () => {
    try {
      const d = await api.draft(e.id);
      setData(d);
      setDraft(structuredClone(d.revision.payload.draft));
    } catch (err) {
      setError(errText(err));
    }
  };
  useEffect(() => {
    void load();
    api.tools().then((t) => setCatalog(t.tools), () => {});
  }, [e.id, detail.state.draftRevisionId]);

  if (!data || !draft) return <ErrorNote error={error ?? null} />;
  const dirty = JSON.stringify(draft) !== JSON.stringify(data.revision.payload.draft);

  const setText = (k: TextKey, v: string) => setDraft({ ...draft, [k]: { ...draft[k], value: v } });
  const existing = new Map(draft.tools.existing.map((t) => [t.value.id, t]));

  const save = async (andReview: boolean) => {
    setBusy(true);
    setError(undefined);
    try {
      let revId = data.revision.id;
      if (dirty) revId = (await api.saveDraft(e.id, data.revision.id, draft)).revision.id;
      if (andReview) await api.review(e.id, revId);
      await reload();
      await load();
      if (andReview) go(`/employees/${e.id}/publish`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-4">
      <SectionTitle
        title="Review"
        description="Read the distilled context as the employee will receive it. Edit anything; each save is kept as a new revision. Mark it reviewed to unlock publishing."
        action={
          <div className="flex items-center gap-2">
            {data.reviewed && !dirty ? <StatusDot tone="success" label="Reviewed" /> : <StatusDot tone="warning" label="Not reviewed" />}
          </div>
        }
      />
      <ErrorNote error={error} />
      {data.stale && (
        <Alert variant="warning">
          <AlertDescription>This draft is stale: distill again to include the newest knowledge and interviews.</AlertDescription>
        </Alert>
      )}

      {draft.openQuestions.length > 0 && (
        <Alert variant="info">
          <AlertDescription>
            <p className="mb-1 font-medium">Open questions the interview did not settle</p>
            <ul className="list-disc pl-5">
              {draft.openQuestions.map((q, i) => (
                <li key={i}>{q}</li>
              ))}
            </ul>
            <p className="mt-1">Answer them in another interview session, or edit the context directly.</p>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="grid gap-5">
          {TEXT_FIELDS.map((f) => (
            <Field key={f.key} label={f.label} hint={f.hint} htmlFor={`draft-${f.key}`}>
              {f.rows === 1 ? (
                <Input id={`draft-${f.key}`} value={draft[f.key].value} onChange={(x) => setText(f.key, x.target.value)} />
              ) : (
                <Textarea id={`draft-${f.key}`} rows={f.rows} value={draft[f.key].value} onChange={(x) => setText(f.key, x.target.value)} />
              )}
              <EvidenceList evidence={draft[f.key].evidence} />
            </Field>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Decision authority</CardTitle>
          <CardDescription>One decision per line.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Decides alone" htmlFor="da-alone">
            <Textarea
              id="da-alone"
              rows={5}
              value={draft.decisionAuthority.value.alone.join("\n")}
              onChange={(x) => setDraft({ ...draft, decisionAuthority: { ...draft.decisionAuthority, value: { ...draft.decisionAuthority.value, alone: lines(x.target.value) } } })}
            />
          </Field>
          <Field label="Escalates" htmlFor="da-escalate">
            <Textarea
              id="da-escalate"
              rows={5}
              value={draft.decisionAuthority.value.escalate.join("\n")}
              onChange={(x) => setDraft({ ...draft, decisionAuthority: { ...draft.decisionAuthority, value: { ...draft.decisionAuthority.value, escalate: lines(x.target.value) } } })}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tools</CardTitle>
          <CardDescription>Tools that exist on the platform today. Checked tools become the employee's capabilities.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3">
          {catalog.map((t) => {
            const on = existing.get(t.id);
            return (
              <div key={t.id} className="flex items-start gap-3">
                <Checkbox
                  id={`tool-${t.id}`}
                  checked={Boolean(on)}
                  onCheckedChange={(v) => {
                    const next = v
                      ? [...draft.tools.existing, { value: { id: t.id, why: "Added during review." }, evidence: [] }]
                      : draft.tools.existing.filter((x) => x.value.id !== t.id);
                    setDraft({ ...draft, tools: { ...draft.tools, existing: next } });
                  }}
                />
                <label htmlFor={`tool-${t.id}`} className="grid gap-0.5 text-sm">
                  <span className="font-mono">{t.id}</span>
                  <span className="text-muted-foreground">{on ? on.value.why : t.summary}</span>
                </label>
              </div>
            );
          })}
          {draft.tools.requested.length > 0 && (
            <div className="mt-2 border-t pt-3">
              <p className="mb-2 text-sm font-medium">Requested tools that do not exist yet</p>
              <ul className="grid gap-2 text-sm">
                {draft.tools.requested.map((t, i) => (
                  <li key={i} className="flex items-start justify-between gap-2">
                    <span>
                      <span className="font-medium">{t.value.name}</span> — <span className="text-muted-foreground">{t.value.why}</span>
                    </span>
                    <Button size="xs" variant="ghost" onClick={() => setDraft({ ...draft, tools: { ...draft.tools, requested: draft.tools.requested.filter((_, j) => j !== i) } })}>
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="sticky bottom-4 flex flex-wrap gap-2 rounded-xl border bg-card/95 p-3 backdrop-blur">
        <Button variant="outline" disabled={busy || !dirty} onClick={() => void save(false)}>
          Save changes
        </Button>
        <Button disabled={busy || (data.reviewed && !dirty)} onClick={() => void save(true)}>
          {dirty ? "Save and mark reviewed" : "Mark reviewed"}
        </Button>
        {dirty && (
          <Button variant="ghost" disabled={busy} onClick={() => setDraft(structuredClone(data.revision.payload.draft))}>
            Discard changes
          </Button>
        )}
      </div>
    </div>
  );
}
